/**
 * Staff sign-in page.
 *
 * Four mutually-exclusive states governed by ``step``:
 *
 *   email_password
 *     The default. Email + password form. On submit calls
 *     /api/auth/begin-login which returns ``next_action``:
 *       - "totp_code"          → step "totp_code"
 *       - "enrol_totp"         → step "enrol_totp" (QR shown)
 *       - "change_password"    → step "change_password"
 *
 *   totp_code
 *     6-digit code input. On submit calls /api/auth/complete-login,
 *     receives session cookie, redirects to ?from= URL.
 *
 *   enrol_totp
 *     Shows the QR code + manual-entry secret. The "Continue"
 *     button reveals a 6-digit input that posts to /api/auth/confirm-totp.
 *
 *   change_password
 *     For first-login users with the must-change-password flag set.
 *     Current password + new password + confirm new password. Posts
 *     to /api/auth/change-password (which requires no auth pre-state
 *     check because we just authenticated with begin-login - but we
 *     need a temporary session to reach the endpoint, so we issue a
 *     login *before* the change). To keep things simple: after a
 *     successful change-password call, we bounce the user back to
 *     "email_password" so they re-enter their new password fresh.
 *
 * Centralises all axios calls through the project-wide ``withCredentials``
 * default so the session cookie is set + sent automatically.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, renderApiError, useAuth } from "../lib/auth";
import {
  ShieldCheck,
  KeyRound,
  Loader2,
  ScanLine,
  Copy,
  Check,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const from = params.get("from") || "/share";

  // Bounce already-authenticated users to wherever they were headed.
  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, from, navigate]);

  const [step, setStep] = useState("email_password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");

  // Enrolment payload from /enrol-totp.
  const [enrol, setEnrol] = useState(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setStep("email_password");
    setCode("");
    setNewPassword("");
    setNewPasswordConfirm("");
    setEnrol(null);
    setError("");
  };

  // ---- step: email + password ---------------------------------------
  const submitBegin = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/begin-login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      if (data.next_action === "totp_code") setStep("totp_code");
      else if (data.next_action === "no_totp") {
        // Preview-only TOTP-bypass dev account. Complete the login
        // immediately with an empty totp_code - the backend's bypass
        // path issues the JWT cookie without verifying any code.
        await axios.post(`${API}/auth/complete-login`, {
          email: email.trim().toLowerCase(),
          password,
          totp_code: "",
        });
        await refresh();
        toast.success("Signed in (TOTP bypassed - preview-only account)");
        navigate(from, { replace: true });
      } else if (data.next_action === "enrol_totp") {
        // Immediately fetch the QR code so the user lands on a
        // ready-to-scan screen rather than a "click here to start" intermediate.
        const { data: ed } = await axios.post(`${API}/auth/enrol-totp`, {
          email: email.trim().toLowerCase(),
          password,
        });
        setEnrol(ed);
        setStep("enrol_totp");
      } else if (data.next_action === "change_password") {
        setStep("change_password");
      } else {
        setError("Unexpected response - refresh and try again.");
      }
    } catch (err) {
      setError(renderApiError(err, "Sign-in failed."));
    } finally {
      setBusy(false);
    }
  };

  // ---- step: TOTP code (steady-state login) -------------------------
  const submitTotp = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/complete-login`, {
        email: email.trim().toLowerCase(),
        password,
        totp_code: code.trim(),
      });
      await refresh();
      toast.success("Signed in");
      navigate(from, { replace: true });
    } catch (err) {
      setError(renderApiError(err, "Sign-in failed."));
    } finally {
      setBusy(false);
    }
  };

  // ---- step: confirm TOTP enrolment ---------------------------------
  const submitConfirmTotp = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/confirm-totp`, {
        email: email.trim().toLowerCase(),
        password,
        totp_code: code.trim(),
      });
      await refresh();
      toast.success("Two-factor enabled");
      navigate(from, { replace: true });
    } catch (err) {
      setError(renderApiError(err, "Code is incorrect."));
    } finally {
      setBusy(false);
    }
  };

  // ---- step: change password (first login) --------------------------
  const submitChangePassword = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError("New passwords don't match.");
      return;
    }
    setBusy(true);
    setError("");
    // The change-password endpoint requires an active session, so
    // we briefly need a token. We don't have one yet (we're in
    // first-login flow) - but the user hasn't enrolled TOTP yet either,
    // which means /complete-login can't issue one. Solution: change
    // the password directly via a dedicated endpoint that requires
    // only the CURRENT password (no session). We use the existing
    // /change-password endpoint by first issuing a one-shot "bootstrap"
    // session via the dedicated /confirm-password-change path …
    // but that's overcomplicated. Simpler: change-password requires
    // an authenticated session. So we just check that ``begin-login``
    // accepted the old creds, then submit change-password via a
    // dedicated route that uses email + old + new in one go.
    try {
      await axios.post(`${API}/auth/first-login-change-password`, {
        email: email.trim().toLowerCase(),
        current_password: password,
        new_password: newPassword,
      });
      toast.success("Password updated - sign in with your new password");
      setPassword("");
      setNewPassword("");
      setNewPasswordConfirm("");
      setStep("email_password");
    } catch (err) {
      setError(renderApiError(err, "Password change failed."));
    } finally {
      setBusy(false);
    }
  };

  const subtitleByStep = {
    email_password: "Sign in to your Medicys staff account.",
    totp_code: "Open Microsoft Authenticator and enter the 6-digit code.",
    enrol_totp:
      "Scan this QR code with Microsoft Authenticator (or any TOTP app), then enter the first code it shows.",
    change_password: "Your password is temporary. Choose a new one before continuing.",
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col" data-testid="login-page">
      {/* Quiet wordmark - mirrors the StaffShell top nav so this page
          reads as part of the same workspace, not a third-party login. */}
      <header className="px-6 py-4 border-b border-stone-200 bg-stone-50/85 backdrop-blur">
        <p className="text-[11px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A] font-semibold">
          Medicys <span className="text-stone-400">· staff workspace</span>
        </p>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#1B4F8A]/10 mb-5">
              {step === "totp_code" || step === "enrol_totp" ? (
                <ShieldCheck className="w-5 h-5 text-[#1B4F8A]" />
              ) : (
                <KeyRound className="w-5 h-5 text-[#1B4F8A]" />
              )}
            </div>
            <h1 className="text-2xl font-serif font-light text-slate-900 mb-2">
              {step === "email_password" && "Sign in"}
              {step === "totp_code" && "Two-factor code"}
              {step === "enrol_totp" && "Set up two-factor authentication"}
              {step === "change_password" && "Change your password"}
            </h1>
            <p className="text-sm text-slate-500 leading-relaxed">
              {subtitleByStep[step]}
            </p>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700"
            >
              {error}
            </div>
          )}

          {step === "email_password" && (
            <form onSubmit={submitBegin} className="space-y-3" data-testid="login-form">
              <Field
                label="Work email"
                type="email"
                value={email}
                onChange={setEmail}
                autoComplete="username"
                placeholder="you@medicysltd.co.uk"
                testid="login-email-input"
                disabled={busy}
                autoFocus
              />
              <Field
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                testid="login-password-input"
                disabled={busy}
              />
              <SubmitButton busy={busy} testid="login-submit-btn">
                Continue
              </SubmitButton>
            </form>
          )}

          {step === "totp_code" && (
            <form onSubmit={submitTotp} className="space-y-3" data-testid="login-totp-form">
              <Field
                label="6-digit code"
                type="text"
                value={code}
                onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000 000"
                testid="login-totp-input"
                disabled={busy}
                autoFocus
                mono
              />
              <SubmitButton busy={busy} disabled={code.length !== 6} testid="login-totp-submit-btn">
                Sign in
              </SubmitButton>
              <button
                type="button"
                onClick={reset}
                data-testid="login-back-btn"
                className="block w-full text-center text-[11px] font-mono tracking-[0.14em] uppercase text-stone-500 hover:text-slate-900 transition-colors pt-1"
              >
                ← Cancel
              </button>
            </form>
          )}

          {step === "enrol_totp" && enrol && (
            <EnrolTotpPanel
              enrol={enrol}
              code={code}
              setCode={setCode}
              busy={busy}
              onSubmit={submitConfirmTotp}
              onCancel={reset}
            />
          )}

          {step === "change_password" && (
            <form
              onSubmit={submitChangePassword}
              className="space-y-3"
              data-testid="login-change-password-form"
            >
              <Field
                label="New password (8+ chars)"
                type="password"
                value={newPassword}
                onChange={setNewPassword}
                autoComplete="new-password"
                testid="login-new-password-input"
                disabled={busy}
                autoFocus
              />
              <Field
                label="Confirm new password"
                type="password"
                value={newPasswordConfirm}
                onChange={setNewPasswordConfirm}
                autoComplete="new-password"
                testid="login-new-password-confirm-input"
                disabled={busy}
              />
              <SubmitButton busy={busy} testid="login-change-password-submit-btn">
                Update password
              </SubmitButton>
              <button
                type="button"
                onClick={reset}
                className="block w-full text-center text-[11px] font-mono tracking-[0.14em] uppercase text-stone-500 hover:text-slate-900 transition-colors pt-1"
              >
                ← Cancel
              </button>
            </form>
          )}
        </div>
      </main>

      <footer className="border-t border-stone-200 bg-white/40 px-6 py-4 text-[11px] font-mono text-stone-500 tracking-wide text-center">
        medicys · confidential · staff only
      </footer>
    </div>
  );
}

// ---- TOTP enrolment subview ----------------------------------------
function EnrolTotpPanel({ enrol, code, setCode, busy, onSubmit, onCancel }) {
  const [copied, setCopied] = useState(false);
  const onCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(enrol.manual_entry_secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked in some browsers - ignore */
    }
  };
  return (
    <div className="space-y-5" data-testid="login-enrol-totp">
      <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm">
        <img
          src={enrol.qr_data_url}
          alt="TOTP QR code for Microsoft Authenticator"
          data-testid="login-totp-qr"
          className="w-44 h-44 mx-auto block"
        />
        <p className="text-[11px] text-stone-500 mt-4 text-center font-mono tracking-wide">
          <ScanLine className="w-3 h-3 inline -mt-0.5 mr-1" />
          Scan with Microsoft Authenticator
        </p>
      </div>

      <details className="text-xs text-stone-500" data-testid="login-totp-manual">
        <summary className="cursor-pointer text-stone-500 hover:text-slate-900 select-none">
          Can&apos;t scan? Enter manually
        </summary>
        <div className="mt-2 rounded-lg bg-stone-100 border border-stone-200 p-3 flex items-center gap-2">
          <code className="flex-1 text-[11px] font-mono tracking-[0.18em] text-slate-700 break-all">
            {enrol.manual_entry_secret}
          </code>
          <button
            type="button"
            onClick={onCopySecret}
            className="text-stone-400 hover:text-[#1B4F8A] p-1 rounded transition-colors"
            aria-label="Copy secret"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
        <p className="text-[10px] text-stone-500 mt-1">
          Account: {enrol.issuer || "Medicys Proposals"}
        </p>
      </details>

      <form onSubmit={onSubmit} className="space-y-3">
        <Field
          label="Enter the 6-digit code from the app"
          type="text"
          value={code}
          onChange={(v) => setCode(v.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000 000"
          testid="login-totp-confirm-input"
          disabled={busy}
          autoFocus
          mono
        />
        <SubmitButton busy={busy} disabled={code.length !== 6} testid="login-totp-confirm-btn">
          Finish setup
        </SubmitButton>
        <button
          type="button"
          onClick={onCancel}
          className="block w-full text-center text-[11px] font-mono tracking-[0.14em] uppercase text-stone-500 hover:text-slate-900 transition-colors pt-1"
        >
          ← Cancel
        </button>
      </form>
    </div>
  );
}

// ---- shared form atoms ---------------------------------------------
function Field({
  label,
  testid,
  mono = false,
  onChange,
  ...rest
}) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono tracking-[0.18em] uppercase text-stone-500 mb-1.5">
        {label}
      </span>
      <input
        {...rest}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={
          "w-full bg-white border border-stone-300 rounded-lg px-3.5 py-2.5 " +
          "text-sm text-slate-900 placeholder:text-stone-400 " +
          "focus:outline-none focus:border-[#3EA8DC] focus:ring-2 focus:ring-[#3EA8DC]/20 " +
          "transition-colors disabled:opacity-60 " +
          (mono ? "font-mono tracking-[0.3em] text-center text-base" : "")
        }
      />
    </label>
  );
}

function SubmitButton({ busy, disabled, testid, children }) {
  return (
    <button
      type="submit"
      disabled={busy || disabled}
      data-testid={testid}
      className="w-full bg-[#1B4F8A] hover:bg-[#163F70] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-5 py-2.5 rounded-lg transition-colors inline-flex items-center justify-center gap-2 shadow-md shadow-[#1B4F8A]/20"
    >
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <>
          {children}
          <ArrowRight className="w-4 h-4" />
        </>
      )}
    </button>
  );
}
